/*
 * Ryan O'Dowd
 * 2025-05-26
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  useEffect,
  useMemo,
} from 'react';
import CPassage from '@classes/Passage';
import routing from '@utils/routing';

// chatgpt
function _prepareQueryParams(params) {
  const result = {};

  for (const key in params) {
    const value = params[key];

    if (value === undefined) {
      continue;
    } else if (typeof value === 'object' && value !== null && (Array.isArray(value) || Object.prototype.toString.call(value) === '[object Object]')) {
      result[key] = JSON.stringify(value);
    } else {
      result[key] = String(value); // ensure primitives are strings
    }
  }

  return result;
}

// chatgpt
function _convertQueryParamsBack(value) {
  if (Array.isArray(value)) {
    return value.map(_convertQueryParamsBack);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [key, _convertQueryParamsBack(val)]),
    );
  }

  if (typeof value === 'string') {
    if (value === 'true') return true;
    if (value === 'false') return false;
    if (value === 'null') return null;
    if (!isNaN(Number(value)) && value.trim() !== '') return Number(value);

    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object' || Array.isArray(parsed)) {
        return _convertQueryParamsBack(parsed);
      }
    } catch (__e) {
      // Not a valid JSON string — leave as-is
    }
  }

  return value;
}

export default function useLocationState() {
  const location = routing.useLocation();
  const params = routing.useParams();
  const [_searchParams, setSearchParams] = routing.useSearchParams();
  const queryParams = useMemo(() => {
    return Object.fromEntries(_searchParams);
  }, [_searchParams]);

  const rawState = useMemo(() => {
    return {
      ...(location.hash ? {hash: location.hash} : {}),
      ...queryParams,
      ...location.state,
      ...params,
    };
  }, [location.hash, location.state, params, queryParams]);

  const preparedQueryParams = useMemo(() => {
    return _prepareQueryParams(rawState);
  }, [rawState]);

  useEffect(() => {
    const prevKeys = Object.keys(queryParams);
    const nextKeys = Object.keys(preparedQueryParams);

    const keysChanged = (prevKeys.length !== nextKeys.length) || (!nextKeys.every((key) => preparedQueryParams[key] === queryParams[key]));

    if (keysChanged) {
      // make sure all state in in query params
      setSearchParams(preparedQueryParams, {replace: true}); // @TODO: error in email auth (still?)
    }
  }, [preparedQueryParams, queryParams, setSearchParams]);

  const restored = useMemo(() => {
    // convert bools, numbers, and objects back
    const state = _convertQueryParamsBack(preparedQueryParams);

    // @TODO: this is too hacky for preserving classes through navigation state {
    if ('passage' in state) {
      state.passage = CPassage.createFromObject(state.passage);
    }
    if ('passages' in state) {
      state.passages = state.passages.map((p) => CPassage.createFromObject(p));
    }
    // @TODO: this is too hacky for preserving classes through navigation state }

    return state;
  }, [preparedQueryParams]);

  return restored;

  /* @TODO: chatgpt is recommending this because the dep arrays above may not have stable references...i think it's fine
  // deep memoize to preserve referential identity unless contents change
  const stableRef = useRef(restored);
  if (JSON.stringify(stableRef.current) !== JSON.stringify(restored)) {
    stableRef.current = restored;
  }

  return stableRef.current;
  */
}
